import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { calibrationService, DEFAULT_GROUP_KEY } from "../../../../../../services/CalibrationService";
import type { RootState } from "../store";
import type {
    AnnotatedDataset,
    ClientAssetDataDataset,
    ClientAssetDataStructure,
    ClientAssetDataSpan,
    DevicesDataset,
    Device,
    WeatherSensor,
    LatLong,
} from "../../../types";
import { Draft } from "@reduxjs/toolkit";

export type Status = "idle" | "loading" | "succeeded" | "failed";
export type DeviceType = "edge-device" | "weather-sensor";

export function getRoundedCurrentDatetime() {
    const date = new Date();
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
}

export interface DatasetsState {
    // basics - client and project, and related datasets
    client: string;
    project: string;
    clientAssetDataDatasetStatus: Status;
    clientAssetDataDataset?: AnnotatedDataset<ClientAssetDataDataset>;
    deviceDataDatasetStatus: Status;
    deviceDataDataset?: AnnotatedDataset<DevicesDataset>;

    // Position shared across the application
    currentPosition?: LatLong;

    // Used for add/editing/deleting devices
    deviceUpdateStatus: "idle" | "loading" | "failed";
    deviceUpdateError?: string;
    currentDeviceTypeBeingEdited?: DeviceType;
    currentDeviceBeingEdited?: Partial<Device> | Partial<WeatherSensor>;
    existingDeviceIdBeingEdited?: number | string; // edge_device_id or device_sn

    // The visible spans of the current device being edited -- needs to be
    // loaded separately
    visibleSpans?: ClientAssetDataSpan[];
    visibleSpansStatus: Status;

    // undefined means unset, null means clear value, otherwise it's a date string
    onlineDateOverrideUncommittedValue?: string | null;
    onlineDateOverrideStatus: Status;
    onlineDateOverrideError?: string;
}

function resolveStructureNameAtPosition(state: DatasetsState): string | undefined {
    const pos = state.currentPosition;
    if (!pos?.latitude || !pos?.longitude) return undefined;
    const structure = state.clientAssetDataDataset?.data.structures.find(
        (s: ClientAssetDataStructure) => s.latitude === pos.latitude && s.longitude === pos.longitude
    );
    return structure?.structure_name;
}

// Calculate distance between two lat/long points in meters using Haversine formula
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
}

function determineSpanPosition(span: { start_structure: string; end_structure: string }, clientAssetData: object) {
    // We can't type this because it hasn't been fully formed yet
    const structures = (clientAssetData as any).data.structures;
    const structureMap = new Map<string, ClientAssetDataStructure>();
    structures.forEach((structure: ClientAssetDataStructure) => {
        structureMap.set(structure.structure_name, structure);
    });

    const startStructure = structureMap.get(span.start_structure);
    const endStructure = structureMap.get(span.end_structure);
    if (startStructure && endStructure) {
        const latitude = (startStructure.latitude + endStructure.latitude) / 2;
        const longitude = (startStructure.longitude + endStructure.longitude) / 2;
        return {
            latitude,
            longitude,
        };
    }
    return undefined;
}

export function determineSpanName(span: { start_structure: string; end_structure: string; section_name: string }) {
    return `${span.start_structure}-${span.end_structure} (${span.section_name})`;
}

const initialState = {
    client: "",
    project: "",
    clientAssetDataDatasetStatus: "idle",
    clientAssetDataDataset: undefined,
    deviceDataDatasetStatus: "idle",
    deviceDataDataset: undefined,
    currentPosition: undefined,
    deviceUpdateStatus: "idle",
    deviceUpdateError: undefined,
    currentDeviceTypeBeingEdited: undefined,
    currentDeviceBeingEdited: undefined,
    existingDeviceIdBeingEdited: undefined,
    visibleSpans: undefined,
    visibleSpansStatus: "idle",

    onlineDateOverrideUncommittedValue: undefined,
    onlineDateOverrideStatus: "idle",
    onlineDateOverrideError: undefined,
} as DatasetsState;

function createLoadLatestDatasetAsyncThunkCreator(datasetType: string) {
    return createAsyncThunk(`datasets/loadLatestDataset/${datasetType}`, async (_, thunkAPI) => {
        const { client, project } = (thunkAPI.getState() as RootState).datasets;
        if (client && project) {
            return await calibrationService.getLatestDatasetViaSignedUrl(client, project, DEFAULT_GROUP_KEY, datasetType, false);
        }
        // Note the unstated case here where nothing happens if client or
        // project is not set. An undefined payload should be effectively the
        // same as a dataset reset! It means no valid dataset is determinable!
    });
}

export const loadLatestClientAssetDataDataset = createLoadLatestDatasetAsyncThunkCreator("client-asset-data");
export const loadLatestDeviceDataDataset = createLoadLatestDatasetAsyncThunkCreator("device-data");

const extractDatasetVersionDttmAsString = (dataset?: AnnotatedDataset<any>) => {
    return dataset?.meta.version_dttm && new Date(dataset.meta.version_dttm).toISOString().split(".")[0] + "Z";
};

const extractErrorFromRejectedAction = (action: any): string => {
    // This lazy approach shows the full error object instead of providing any
    // field-/form-level error visibility.
    const error = (action.payload as unknown as { error: string | object }).error;
    if (typeof error === "string") {
        return error;
    } else {
        return JSON.stringify(error);
    }
};

export const submitEdgeDeviceChange = createAsyncThunk(
    "datasets/submitEdgeDeviceChange",
    async ({ skipMasterdataSync }: { skipMasterdataSync: boolean }, thunkAPI) => {
        const { client, project, deviceDataDataset, currentDeviceBeingEdited, existingDeviceIdBeingEdited } = (
            thunkAPI.getState() as RootState
        ).datasets;
        try {
            if (client && project && existingDeviceIdBeingEdited) {
                await calibrationService.submitEdgeDeviceEdit(
                    client,
                    project,
                    extractDatasetVersionDttmAsString(deviceDataDataset),
                    existingDeviceIdBeingEdited,
                    currentDeviceBeingEdited,
                    skipMasterdataSync
                );
            } else if (client && project && !existingDeviceIdBeingEdited) {
                await calibrationService.submitEdgeDeviceAdd(
                    client,
                    project,
                    extractDatasetVersionDttmAsString(deviceDataDataset),
                    currentDeviceBeingEdited,
                    skipMasterdataSync
                );
            }
        } catch (e) {
            throw thunkAPI.rejectWithValue(e);
        }
    }
);

export const submitWeatherSensorChange = createAsyncThunk(
    "datasets/submitWeatherSensorChange",
    async ({ skipMasterdataSync }: { skipMasterdataSync: boolean }, thunkAPI) => {
        const { client, project, deviceDataDataset, currentDeviceBeingEdited, existingDeviceIdBeingEdited } = (
            thunkAPI.getState() as RootState
        ).datasets;
        try {
            if (client && project && existingDeviceIdBeingEdited) {
                await calibrationService.submitWeatherSensorEdit(
                    client,
                    project,
                    extractDatasetVersionDttmAsString(deviceDataDataset),
                    existingDeviceIdBeingEdited,
                    currentDeviceBeingEdited,
                    skipMasterdataSync
                );
            } else if (client && project && !existingDeviceIdBeingEdited) {
                await calibrationService.submitWeatherSensorAdd(
                    client,
                    project,
                    extractDatasetVersionDttmAsString(deviceDataDataset),
                    currentDeviceBeingEdited,
                    skipMasterdataSync
                );
            }
        } catch (e) {
            throw thunkAPI.rejectWithValue(e);
        }
    }
);

export const submitEdgeDeviceDelete = createAsyncThunk(
    "datasets/submitEdgeDeviceDelete",
    async ({ edgeDeviceId, skipMasterdataSync }: { edgeDeviceId: number; skipMasterdataSync: boolean }, thunkAPI) => {
        const { client, project, deviceDataDataset } = (thunkAPI.getState() as RootState).datasets;
        try {
            return await calibrationService.submitEdgeDeviceDelete(
                client,
                project,
                extractDatasetVersionDttmAsString(deviceDataDataset),
                edgeDeviceId,
                skipMasterdataSync
            );
        } catch (e) {
            return thunkAPI.rejectWithValue(e);
        }
    }
);
export const submitWeatherSensorDelete = createAsyncThunk(
    "datasets/submitWeatherSensorDelete",
    async (
        { weatherSensorSn, skipMasterdataSync }: { weatherSensorSn: string; skipMasterdataSync: boolean },
        thunkAPI
    ) => {
        const { client, project, deviceDataDataset } = (thunkAPI.getState() as RootState).datasets;
        try {
            return await calibrationService.submitWeatherSensorDelete(
                client,
                project,
                extractDatasetVersionDttmAsString(deviceDataDataset),
                weatherSensorSn,
                skipMasterdataSync
            );
        } catch (e) {
            return thunkAPI.rejectWithValue(e);
        }
    }
);

export const submitDeviceDatasetOnlineDateOverride = createAsyncThunk(
    "datasets/submitDeviceDatasetOnlineDateOverride",
    async (_, thunkAPI) => {
        const { client, project, deviceDataDataset, onlineDateOverrideUncommittedValue } = (
            thunkAPI.getState() as RootState
        ).datasets;
        if (onlineDateOverrideUncommittedValue === undefined) {
            return thunkAPI.rejectWithValue("No online date override value set");
        }
        try {
            return await calibrationService.submitDeviceDatasetOnlineDateOverride(
                client,
                project,
                extractDatasetVersionDttmAsString(deviceDataDataset),
                onlineDateOverrideUncommittedValue
            );
        } catch (e) {
            return thunkAPI.rejectWithValue(e);
        }
    }
);
const DEFAULT_DEVICE_VIEW_DISTANCE = 10;
const DEFAULT_DEVICE_VIEW_ANGLE = 95;
const DEFAULT_WEATHER_SENSOR_ANEMOMETER_HEADING = 0;
const DEFAULT_WEATHER_SENSOR_MOUNTING_ARM_LENGTH = 0.91; // 3 feet in meters

export const loadEditingDeviceVisibleSpans = createAsyncThunk("datasets/queryVisibleSpans", async (_, thunkAPI) => {
    const {
        client,
        project,
        clientAssetDataDataset,
        currentPosition,
        currentDeviceTypeBeingEdited,
        currentDeviceBeingEdited,
    } = (thunkAPI.getState() as RootState).datasets;
    if (currentDeviceTypeBeingEdited == "edge-device") {
        return (
            (await calibrationService.queryVisibleSpans(
                client,
                project,
                new Date((clientAssetDataDataset?.meta as any)?.version_dttm).toISOString().split(".")[0] + "Z",
                currentPosition?.latitude,
                currentPosition?.longitude,
                (currentDeviceBeingEdited as Partial<Device>).approx_angle ?? 0,
                DEFAULT_DEVICE_VIEW_DISTANCE,
                DEFAULT_DEVICE_VIEW_ANGLE
            )) as any
        ).data;
    }
    // If current device is not an edge device, do nothing
});

function setPositionToFoundObject<ObjType extends LatLong>(
    state: Draft<DatasetsState>,
    array: ObjType[],
    predicate: (val: ObjType) => boolean
) {
    const found = array.find(predicate);
    if (found) {
        state.currentPosition = {
            latitude: found.latitude,
            longitude: found.longitude,
        };
    }
}

export const datasetsSlice = createSlice({
    name: "datasets",
    initialState,
    reducers: {
        setClient: (state, action: PayloadAction<string>) => {
            state.client = action.payload;
            state.currentDeviceBeingEdited = undefined;
        },
        setProject: (state, action: PayloadAction<string>) => {
            state.project = action.payload;
            state.currentDeviceBeingEdited = undefined;
        },
        // TODO: The use of the word select is confusing, change it
        setActiveStructureByName: (state, action: PayloadAction<string>) => {
            state.clientAssetDataDataset &&
                setPositionToFoundObject(
                    state,
                    state.clientAssetDataDataset.data.structures,
                    (s) => s.structure_name === action.payload
                );
        },
        setActiveSpanByName: (state, action: PayloadAction<string>) => {
            state.clientAssetDataDataset &&
                setPositionToFoundObject(
                    state,
                    state.clientAssetDataDataset.data.spans,
                    (s) => s.span_name === action.payload
                );
        },
        setActiveEdgeDeviceById: (state, action: PayloadAction<number>) => {
            state.deviceDataDataset &&
                setPositionToFoundObject(
                    state,
                    state.deviceDataDataset?.data.devices.map((d) => ({
                        edge_device_id: d.edge_device_id,
                        latitude: d.position.latitude,
                        longitude: d.position.longitude,
                    })),
                    (d: { edge_device_id: number } & LatLong) => d.edge_device_id === action.payload
                );
        },
        setActiveWeatherSensorBySn: (state, action: PayloadAction<string>) => {
            state.deviceDataDataset &&
                setPositionToFoundObject(
                    state,
                    state.deviceDataDataset?.data.weather_sensors.map((d) => ({
                        device_sn: d.device_sn,
                        latitude: d.position.latitude,
                        longitude: d.position.longitude,
                    })),
                    (d: { device_sn: string } & LatLong) => d.device_sn === action.payload
                );
        },
        setCurrentPosition: (state, action: PayloadAction<LatLong>) => {
            const targetLat = action.payload.latitude;
            const targetLon = action.payload.longitude;

            // If we have structure data, check if we're within 10 meters of any structure
            if (state.clientAssetDataDataset && targetLat !== undefined && targetLon !== undefined) {
                const SNAP_DISTANCE_METERS = 10;
                let closestStructure: ClientAssetDataStructure | undefined;
                let closestDistance = Infinity;

                // Find the closest structure
                for (const structure of state.clientAssetDataDataset.data.structures) {
                    const distance = calculateDistance(targetLat, targetLon, structure.latitude, structure.longitude);
                    if (distance < closestDistance) {
                        closestDistance = distance;
                        closestStructure = structure;
                    }
                }

                // If closest structure is within snap distance, use its position
                if (closestStructure && closestDistance <= SNAP_DISTANCE_METERS) {
                    state.currentPosition = {
                        latitude: closestStructure.latitude,
                        longitude: closestStructure.longitude,
                    };
                    return;
                }
            }

            // Otherwise, use the exact position provided
            state.currentPosition = action.payload;
        },
        resetCurrentPosition: (state, action: PayloadAction) => {
            state.currentPosition = undefined;
        },
        resetClientAssetDataDataset: (state, action: PayloadAction) => {
            state.clientAssetDataDataset = undefined;
        },
        resetDeviceDataDataset: (state, action: PayloadAction) => {
            state.deviceDataDataset = undefined;
        },
        setCurrentDeviceBeingEdited: (state, action: PayloadAction<Partial<Device> | Partial<WeatherSensor>>) => {
            state.currentDeviceBeingEdited = action.payload;
        },
        setTargetStructureForDevice: (state, action: PayloadAction<string>) => {
            const structureName = action.payload;
            const structure = state.clientAssetDataDataset?.data.structures.find(
                (s) => s.structure_name === structureName
            );

            if (!structure || !state.currentPosition || !state.currentDeviceBeingEdited) {
                return;
            }

            // Only edge devices have approx_angle
            if (state.currentDeviceTypeBeingEdited !== "edge-device") {
                return;
            }

            // Calculate bearing from device position to structure
            const lat1 = state.currentPosition.latitude!;
            const lon1 = state.currentPosition.longitude!;
            const lat2 = structure.latitude;
            const lon2 = structure.longitude;

            const φ1 = (lat1 * Math.PI) / 180;
            const φ2 = (lat2 * Math.PI) / 180;
            const Δλ = ((lon2 - lon1) * Math.PI) / 180;

            const y = Math.sin(Δλ) * Math.cos(φ2);
            const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
            const θ = Math.atan2(y, x);
            const bearing = ((θ * 180) / Math.PI + 360) % 360;

            (state.currentDeviceBeingEdited as Partial<Device>).approx_angle = Math.round(bearing);
        },
        startEditingNewEdgeDevice: (state) => {
            const defaultInstallDate = new Date();
            defaultInstallDate.setSeconds(0);
            defaultInstallDate.setMilliseconds(0);

            state.currentDeviceBeingEdited = {
                install_date: defaultInstallDate.toISOString(),
                position: state.currentPosition,
                approx_angle: undefined,
            };
            state.currentDeviceTypeBeingEdited = "edge-device";
            state.existingDeviceIdBeingEdited = undefined;
            state.deviceUpdateStatus = "idle";
            state.deviceUpdateError = undefined;
        },
        startEditingNewWeatherSensor: (state) => {
            const defaultInstallDate = new Date();
            defaultInstallDate.setSeconds(0);
            defaultInstallDate.setMilliseconds(0);

            state.currentDeviceBeingEdited = {
                start_dttm: defaultInstallDate.toISOString(),
                position: state.currentPosition,
                anemometer_heading: DEFAULT_WEATHER_SENSOR_ANEMOMETER_HEADING,
                mounting_arm_length: DEFAULT_WEATHER_SENSOR_MOUNTING_ARM_LENGTH,
                structure_name: resolveStructureNameAtPosition(state),
            };
            state.currentDeviceTypeBeingEdited = "weather-sensor";
            state.existingDeviceIdBeingEdited = undefined;
            state.deviceUpdateStatus = "idle";
            state.deviceUpdateError = undefined;
        },
        startEditingExistingDevice: (state, action: PayloadAction<number>) => {
            state.currentDeviceBeingEdited = {
                ...state.deviceDataDataset?.data.devices.find((device) => device.edge_device_id === action.payload),
            };
            state.currentDeviceTypeBeingEdited = "edge-device";
            state.existingDeviceIdBeingEdited = action.payload;
            state.deviceUpdateStatus = "idle";
            state.deviceUpdateError = undefined;
        },
        startEditingExistingWeatherSensor: (state, action: PayloadAction<string>) => {
            const existingSensor = state.deviceDataDataset?.data.weather_sensors.find(
                (weather_sensor) => weather_sensor.device_sn === action.payload
            );
            state.currentDeviceBeingEdited = {
                ...existingSensor,
                structure_name: existingSensor?.structure_name ?? resolveStructureNameAtPosition(state),
            };
            state.currentDeviceTypeBeingEdited = "weather-sensor";
            state.existingDeviceIdBeingEdited = action.payload;
            state.deviceUpdateStatus = "idle";
            state.deviceUpdateError = undefined;
        },
        stopEditingDevice: (state, action: PayloadAction) => {
            state.currentDeviceTypeBeingEdited = undefined;
            state.currentDeviceBeingEdited = undefined;
            state.existingDeviceIdBeingEdited = undefined;
            state.deviceUpdateStatus = "idle";
            state.deviceUpdateError = undefined;
            state.visibleSpans = undefined;
            state.visibleSpansStatus = "idle";
        },
        setOnlineDateOverrideUncommittedValue(state, action: PayloadAction<string | undefined>) {
            console.log(
                !action.payload,
                state.onlineDateOverrideUncommittedValue,
                state.deviceDataDataset?.data.online_date_override
            );
            if (!action.payload) {
                // set to null or undefined depending on what the committed value is
                state.onlineDateOverrideUncommittedValue = state.deviceDataDataset?.data.online_date_override
                    ? null // deliberately clear the value on save, or...
                    : undefined; // nothing to commit.
            } else {
                state.onlineDateOverrideUncommittedValue = action.payload;
            }
        },
        resetOnlineDateOverride(state) {
            // unconditionally clear value
            state.onlineDateOverrideUncommittedValue = undefined;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(loadLatestClientAssetDataDataset.fulfilled, (state, action) => {
            if (action.payload === undefined) {
                state.clientAssetDataDatasetStatus = "idle";
                state.clientAssetDataDataset = undefined;
                return;
            }
            state.clientAssetDataDatasetStatus = "succeeded";
            state.clientAssetDataDataset = {
                ...action.payload,
                data: {
                    ...action.payload.data,
                    structures: action.payload.data.structures.map((structure: { structure_name: string }) => {
                        return {
                            ...structure,
                            // Include a list of spans that this structure is a
                            // part of for fast lookup later.
                            spans: action.payload.data.spans
                                .filter(
                                    (span: { start_structure: string; end_structure: string }) =>
                                        span.start_structure === structure.structure_name ||
                                        span.end_structure === structure.structure_name
                                )
                                .map((span: { start_structure: string; end_structure: string; section_name: string }) =>
                                    determineSpanName(span)
                                ),
                        };
                    }),
                    spans: action.payload.data.spans.map(
                        (span: { start_structure: string; end_structure: string; section_name: string }) => {
                            const span_name = determineSpanName(span);
                            const span_position = determineSpanPosition(span, action.payload);
                            return {
                                span_name: span_name,
                                latitude: span_position?.latitude,
                                longitude: span_position?.longitude,
                                ...span,
                            };
                        }
                    ),
                } as ClientAssetDataDataset,
            };
        });
        builder.addCase(loadLatestDeviceDataDataset.fulfilled, (state, action) => {
            if (action.payload === undefined) {
                state.deviceDataDatasetStatus = "idle";
                state.deviceDataDataset = undefined;
                return;
            }
            state.deviceDataDatasetStatus = "succeeded";
            state.deviceDataDataset = action.payload;

            state.onlineDateOverrideUncommittedValue = undefined;
            state.onlineDateOverrideStatus = "idle";
            state.onlineDateOverrideError = undefined;
        });
        builder.addCase(loadLatestClientAssetDataDataset.pending, (state) => {
            state.clientAssetDataDatasetStatus = "loading";
        });
        builder.addCase(loadLatestDeviceDataDataset.pending, (state) => {
            state.deviceDataDatasetStatus = "loading";
        });
        builder.addCase(loadLatestClientAssetDataDataset.rejected, (state) => {
            state.clientAssetDataDatasetStatus = "failed";
            state.clientAssetDataDataset = undefined;
        });
        builder.addCase(loadLatestDeviceDataDataset.rejected, (state) => {
            state.deviceDataDatasetStatus = "failed";
            state.deviceDataDataset = undefined;
        });
        builder.addCase(submitEdgeDeviceChange.fulfilled, (state) => {
            state.currentDeviceTypeBeingEdited = undefined;
            state.currentDeviceBeingEdited = undefined;
            state.existingDeviceIdBeingEdited = undefined;
            state.deviceUpdateStatus = "idle";
            state.deviceUpdateError = undefined;
            state.visibleSpans = undefined;
            state.visibleSpansStatus = "idle";
        });
        builder.addCase(submitEdgeDeviceChange.rejected, (state, action) => {
            state.deviceUpdateStatus = "failed";
            state.deviceUpdateError = extractErrorFromRejectedAction(action);
        });
        builder.addCase(submitEdgeDeviceChange.pending, (state) => {
            state.deviceUpdateStatus = "loading";
        });
        builder.addCase(submitWeatherSensorChange.fulfilled, (state) => {
            state.currentDeviceTypeBeingEdited = undefined;
            state.currentDeviceBeingEdited = undefined;
            state.existingDeviceIdBeingEdited = undefined;
            state.deviceUpdateStatus = "idle";
            state.deviceUpdateError = undefined;
        });
        builder.addCase(submitWeatherSensorChange.rejected, (state, action) => {
            state.deviceUpdateStatus = "failed";
            state.deviceUpdateError = extractErrorFromRejectedAction(action);
        });
        builder.addCase(submitWeatherSensorChange.pending, (state) => {
            state.deviceUpdateStatus = "loading";
        });
        builder.addCase(submitEdgeDeviceDelete.fulfilled, (state) => {
            state.currentDeviceTypeBeingEdited = undefined;
            state.currentDeviceBeingEdited = undefined;
            state.existingDeviceIdBeingEdited = undefined;
            state.deviceUpdateStatus = "idle";
            state.deviceUpdateError = undefined;
        });
        builder.addCase(submitEdgeDeviceDelete.rejected, (state, action) => {
            state.deviceUpdateStatus = "failed";
            state.deviceUpdateError = extractErrorFromRejectedAction(action);
        });
        builder.addCase(submitEdgeDeviceDelete.pending, (state) => {
            state.deviceUpdateStatus = "loading";
        });
        builder.addCase(submitWeatherSensorDelete.fulfilled, (state) => {
            state.currentDeviceTypeBeingEdited = undefined;
            state.currentDeviceBeingEdited = undefined;
            state.existingDeviceIdBeingEdited = undefined;
            state.deviceUpdateStatus = "idle";
            state.deviceUpdateError = undefined;
        });
        builder.addCase(submitWeatherSensorDelete.rejected, (state, action) => {
            state.deviceUpdateStatus = "failed";
            state.deviceUpdateError = extractErrorFromRejectedAction(action);
        });
        builder.addCase(submitWeatherSensorDelete.pending, (state) => {
            state.deviceUpdateStatus = "loading";
        });
        builder.addCase(loadEditingDeviceVisibleSpans.fulfilled, (state, action) => {
            state.visibleSpansStatus = "succeeded";
            state.visibleSpans = action.payload;

            // Merge API-returned visible spans into the device being edited,
            // preserving any existing user selections and defaulting new spans
            // to targeted_by_sensor: true.
            if (state.currentDeviceBeingEdited && action.payload) {
                const existingSpans = (state.currentDeviceBeingEdited as Partial<Device>).visible_spans || [];
                const mergedSpans = action.payload.map((apiSpan: ClientAssetDataSpan) => {
                    const existing = existingSpans.find(
                        (s) => s.section_name === apiSpan.section_name
                    );
                    return existing ?? {
                        start_structure: apiSpan.start_structure,
                        end_structure: apiSpan.end_structure,
                        section_name: apiSpan.section_name,
                        targeted_by_sensor: true,
                    };
                });
                (state.currentDeviceBeingEdited as Partial<Device>).visible_spans = mergedSpans;
            }
        });
        builder.addCase(loadEditingDeviceVisibleSpans.rejected, (state) => {
            state.visibleSpansStatus = "failed";
        });
        builder.addCase(loadEditingDeviceVisibleSpans.pending, (state) => {
            state.visibleSpansStatus = "loading";
        });
        builder.addCase(submitDeviceDatasetOnlineDateOverride.fulfilled, (state) => {
            state.onlineDateOverrideStatus = "succeeded";
            state.onlineDateOverrideError = undefined;
            state.onlineDateOverrideUncommittedValue = undefined;
        });
        builder.addCase(submitDeviceDatasetOnlineDateOverride.rejected, (state, action) => {
            state.onlineDateOverrideStatus = "failed";
            state.onlineDateOverrideError = (action.payload as unknown as { error: string }).error;
        });
        builder.addCase(submitDeviceDatasetOnlineDateOverride.pending, (state) => {
            state.onlineDateOverrideStatus = "loading";
        });
    },
});

export const {
    setClient,
    setProject,
    setActiveStructureByName,
    setActiveSpanByName,
    setActiveEdgeDeviceById,
    setActiveWeatherSensorBySn,
    setCurrentPosition,
    resetCurrentPosition,
    resetClientAssetDataDataset,
    resetDeviceDataDataset,
    setCurrentDeviceBeingEdited,
    setTargetStructureForDevice,
    startEditingNewEdgeDevice,
    startEditingNewWeatherSensor,
    startEditingExistingDevice,
    startEditingExistingWeatherSensor,
    stopEditingDevice,
    setOnlineDateOverrideUncommittedValue,
    resetOnlineDateOverride,
} = datasetsSlice.actions;

export const selectCurrentClient = (state: RootState) => state.datasets.client;
export const selectCurrentProject = (state: RootState) => state.datasets.project;
export const selectClientAssetDataDatasetStatus = (state: RootState) => state.datasets.clientAssetDataDatasetStatus;
export const selectClientAssetData = (state: RootState) => state.datasets.clientAssetDataDataset;
export const selectDeviceDataDatasetStatus = (state: RootState) => state.datasets.deviceDataDatasetStatus;
export const selectDeviceData = (state: RootState) => state.datasets.deviceDataDataset;
export const selectCurrentPosition = (state: RootState) => state.datasets.currentPosition;
export const selectDeviceUpdateStatus = (state: RootState) => state.datasets.deviceUpdateStatus;
export const selectDeviceUpdateError = (state: RootState) => state.datasets.deviceUpdateError;
export const selectCurrentDeviceTypeBeingEdited = (state: RootState) => state.datasets.currentDeviceTypeBeingEdited;
export const selectCurrentDeviceBeingEdited = (state: RootState) => state.datasets.currentDeviceBeingEdited;
export const selectExistingDeviceIdBeingEdited = (state: RootState) => state.datasets.existingDeviceIdBeingEdited;
export const selectVisibleSpans = (state: RootState) => state.datasets.visibleSpans;
export const selectVisibleSpansStatus = (state: RootState) => state.datasets.visibleSpansStatus;
export const selectOnlineDateOverrideValue = (state: RootState) =>
    state.datasets.onlineDateOverrideUncommittedValue !== undefined
        ? state.datasets.onlineDateOverrideUncommittedValue
        : state.datasets.deviceDataDataset?.data.online_date_override;

export const selectOnlineDateOverrideCommittedValue = (state: RootState) =>
    state.datasets.deviceDataDataset?.data.online_date_override;

export const selectOnlineDateOverrideStatus = (state: RootState) => state.datasets.onlineDateOverrideStatus;
export const selectOnlineDateOverrideCommitWillChangeValue = (state: RootState) =>
    state.datasets.onlineDateOverrideUncommittedValue !== undefined &&
    state.datasets.onlineDateOverrideUncommittedValue !== state.datasets.deviceDataDataset?.data?.online_date_override;

export const selectDeviceDataDatasetVersionDttm = createSelector([selectDeviceData], extractDatasetVersionDttmAsString);
export const selectClientAssetDataDatasetVersionDttm = createSelector(
    [selectClientAssetData],
    extractDatasetVersionDttmAsString
);

export const selectActiveObject = createSelector(
    [selectCurrentPosition, selectClientAssetData],
    (currentPosition, clientAssetDataDataset) => {
        if (!currentPosition?.latitude || !currentPosition?.longitude) {
            return undefined;
        }

        const data: { [index: string]: ClientAssetDataStructure | ClientAssetDataSpan } = {};
        clientAssetDataDataset?.data.structures.forEach((structure) => {
            data[`${structure.latitude.toString()},${structure.longitude.toString()}`] = structure;
        });
        clientAssetDataDataset?.data.spans.forEach((span) => {
            data[`${span.latitude.toString()},${span.longitude.toString()}`] = span;
        });
        return data[currentPosition?.latitude.toString() + "," + currentPosition?.longitude.toString()] || undefined;
    }
);

export const selectActiveSpan = createSelector([selectActiveObject], (activeObject) => {
    if (activeObject && "span_name" in activeObject) {
        return activeObject;
    }
    return undefined;
});

export const selectActiveStructure = createSelector([selectActiveObject], (activeObject) => {
    if (activeObject && "structure_name" in activeObject) {
        return activeObject;
    }
    return undefined;
});

export const selectActiveEdgeDevices = createSelector(
    [selectDeviceData, selectCurrentPosition],
    (deviceData, currentPosition) => {
        const activeEdgeDevices = deviceData?.data?.devices?.filter((edge_device) => {
            return (
                edge_device.position.latitude === currentPosition?.latitude &&
                edge_device.position.longitude === currentPosition?.longitude
            );
        });
        return activeEdgeDevices || [];
    }
);

export const selectActiveWeatherSensors = createSelector(
    [selectDeviceData, selectCurrentPosition],
    (deviceData, currentPosition) => {
        const activeWeatherSensors = deviceData?.data?.weather_sensors?.filter((weather_sensor) => {
            return (
                weather_sensor.position.latitude === currentPosition?.latitude &&
                weather_sensor.position.longitude === currentPosition?.longitude
            );
        });
        return activeWeatherSensors || [];
    }
);

export default datasetsSlice;
